import { useTranslation } from "react-i18next";
import styled from "styled-components";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import Hyperlink from "@ingka/hyperlink";
import Button from "@ingka/button";
import {
  getSelectedEvent,
  getSelectedTimeslot,
  getUser,
  setError,
  useAppContext,
  setEvent as setAppStateEvent,
  isDigitalEvent,
} from "../../contextProviders/AppContext";
import Message from "../../components/Message";
import { config } from "../../config";
import { getCredentialUrl, getEventIdFromSlug } from "../../utils";
import Guests from "../../components/Register/Guests";
import UserDetails from "../../components/Register/UserDetails";
import { CSSEvent, RegisterObject } from "../../types/model";
import ConditionalRender from "../../components/ConditionalRender";
import { getEvent, registerConsumer } from "../../services/storeEventApi";
import SmallCard from "../../components/Card/Small";
import useScrollToTop from "../../hooks/ScrollToTop";
import Focus from "../../components/Focus";
import Reminders from "./Reminders";
import { sendError, sendRemindersEvent, sendSuccessEvent } from "./analytics";
import { REGISTERED, WAITING_LIST } from "./enums";
import Seo from "../../components/Seo/Seo";
import { REGISTER } from "../../components/Seo/enums";

const Wrapper = styled.div`
  display: grid;
  column-gap: var(--space-100);
  row-gap: var(--space-200);

  > div:first-child {
    grid-row: 2;
  }

  > div:last-child {
    grid-row: 1;
  }

  ${({ theme }) => `${theme.breakpoints.lg} {
    grid-template-columns: 1fr 1fr;
    row-gap: var(--space-400);

    > div:last-child {
      grid-column: 1;
    }

    > div:first-child {
      grid-row:1;
      grid-column: 2;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
  }`}
`;

const RegisterButton = styled(Button)`
  width: 100%;

  ${({ theme }) => `${theme.breakpoints.lg} {
      width: unset;
  }`}
`;

const EventName = styled.h2`
  margin: var(--space-50) 0 var(--space-250) 0;
`;

const MessageWrapper = styled.div`
  margin-bottom: var(--space-250);
`;

const Register = () => {
  const [event, setEvent] = useState<CSSEvent | null>(null);
  const [additional, setAdditional] = useState({ guests: 0, children: 0 });

  const [reminders, setReminders] = useState<string[]>([]);
  const { useSelector, dispatch } = useAppContext();
  const { eventid, storeName } = useParams<{
    eventid: string;
    storeName: string;
  }>();
  const { t } = useTranslation();
  const selectedTimeslot = useSelector(getSelectedTimeslot);
  const selectedEvent = useSelector(getSelectedEvent);
  const eventIsDigital = useSelector(isDigitalEvent);
  const user = useSelector(getUser);
  const history = useHistory();
  const { registerToWaitingList, timeslotId } = selectedTimeslot || {};
  const eventId = getEventIdFromSlug(eventid);

  useScrollToTop();

  useEffect(() => {
    if (!selectedEvent) {
      history.replace(`/${storeName}/${eventId}`);
    }
  }, [selectedEvent, eventId, history, storeName]);

  useEffect(() => {
    if (selectedEvent && selectedTimeslot) {
      setEvent({
        ...selectedEvent,
        timeSlots: [selectedTimeslot],
      });
    }
  }, [selectedEvent, selectedTimeslot]);

  if (!event) {
    return null;
  }

  const eventReminders = event?.registrationSettings?.reminders || [];
  const loginUrl = getCredentialUrl(config.editProfileUrl);

  const onChangeQuantity =
    (type: "guests" | "children") =>
    (e: React.FormEvent<HTMLSelectElement>) => {
      const target = e.target as HTMLSelectElement;
      const num = parseInt(target.value);

      setAdditional((s) => ({ ...s, [type]: num }));
    };

  const onChangeReminders = (items: string[]) => {
    setReminders(items);

    sendRemindersEvent(items, event);
  };

  const onRegister = async () => {
    if (!event) {
      throw new Error("No event");
    }
    const registerObject: RegisterObject = {
      contactMethods: reminders,
      state: registerToWaitingList ? WAITING_LIST : REGISTERED,
      adultCount: 1,
      pageLang: config.language,
    };

    if (!registerToWaitingList) {
      registerObject.adultCount = registerObject.adultCount + additional.guests;
      registerObject.childCount = additional.children;
    }

    try {
      await registerConsumer(eventId, timeslotId, registerObject);
      const event = await getEvent(eventId);
      dispatch(setAppStateEvent(event));

      sendSuccessEvent(registerObject, event);

      history.replace(
        `/${storeName}/${eventId}/register/success`,
        registerObject
      );
    } catch (e: any) {
      sendError(e.code);
      dispatch(setError(e.code));
      Sentry.captureException(e);
    }
  };

  return (
    <Focus>
      <Seo page={REGISTER} canonicalPath="/" />
      <Wrapper>
        <div>
          <SmallCard event={event} />
        </div>
        <div>
          <h1>
            {registerToWaitingList
              ? t("register.joinOnWaitingList")
              : t("event.signUp")}
          </h1>
          <EventName>{selectedEvent.eventDetails.eventName}</EventName>
          <p>
            <b>{t("event.welcome", { firstName: user.firstName })}</b>
            <br />
            {t("register.informationCheck")}
          </p>

          <UserDetails user={user} />

          <MessageWrapper>
            <Message subtle>
              {`${t("register.informationBefore")} `}
              <Hyperlink url={loginUrl} text={t("register.informationedit")} />
              {` ${t("register.informationAfter")} `}
            </Message>
          </MessageWrapper>

          <ConditionalRender
            predicate={!registerToWaitingList && !eventIsDigital}
          >
            <Guests onChangeQuantity={onChangeQuantity} />
          </ConditionalRender>

          <Reminders
            onChange={onChangeReminders}
            eventReminders={eventReminders}
          />

          <RegisterButton
            type="primary"
            text={t("register.submit")}
            onClick={onRegister}
          />
        </div>
      </Wrapper>
    </Focus>
  );
};

export default Register;
